import { useSelector } from 'react-redux';
import {
  ExternalProjectContact,
  InternalProjectContact,
  ProjectContact,
} from '../../../models/ProjectContacts';
import {
  ContactId,
  ExternalProjectContactId,
  InternalProjectContactId,
  ProjectId,
} from '../../../models/Types';
import useContactsContext from '../../contacts/hooks/useContactsProvider';
import {
  getExternalProjectContactsData,
  getInternalProjectContactsData,
  RootReducerState,
} from '../../../apps/main/rootReducer';
import * as fromInternalProjectContacts from '../reducers/internalProjectContacts';
import * as fromExternalProjectContacts from '../reducers/externalProjectContacts';
import { createSelector } from 'reselect';
import { useMemo } from 'react';
import { Contact } from '../../../models/Contact';
import { sortContactsHelper } from '../../contacts/utils';
import { distinctByProperty } from '../../../util';

const internalProjectContactsSelector = (projectId: ProjectId) =>
  createSelector<
    [(state: RootReducerState) => fromInternalProjectContacts.DataState],
    InternalProjectContact[]
  >(
    getInternalProjectContactsData,
    (data: fromInternalProjectContacts.DataState) => data[projectId] ?? []
  );

const externalProjectContactsSelector = (projectId: ProjectId) =>
  createSelector<
    [(state: RootReducerState) => fromExternalProjectContacts.DataState],
    ExternalProjectContact[]
  >(
    getExternalProjectContactsData,
    (data: fromExternalProjectContacts.DataState) => {
      return data[projectId] ?? [];
    }
  );

export const useProjectContacts = (
  projectId: ProjectId
): {
  projectContacts: ProjectContact[];
  contacts: Contact[];
  contactIds: ContactId[];
  internalProjectContacts: InternalProjectContact[];
  internalProjectContactIds: InternalProjectContactId[];
  internalProjectContactContacts: Contact[];
  internalProjectContactContactIds: ContactId[];
  externalProjectContactIds: ExternalProjectContactId[];
  externalProjectContacts: ExternalProjectContact[];
  externalProjectContactContactIds: ContactId[];
  externalProjectContactContacts: Contact[];
} => {
  const { getContactById } = useContactsContext();

  const internalProjectsContactData = useSelector(
    internalProjectContactsSelector(projectId)
  );

  const externalProjectContactsData = useSelector(
    externalProjectContactsSelector(projectId)
  );

  return useMemo(() => {
    const internalProjectContacts: InternalProjectContact[] =
      distinctByProperty(
        (internalProjectsContactData ?? [])
          .filter(Boolean)
          .sort(({ contactId: a }, { contactId: b }) =>
            sortContactsHelper(getContactById(a), getContactById(b))
          ),
        'internalProjectContactId'
      );
    const externalProjectContacts: ExternalProjectContact[] =
      distinctByProperty(
        (externalProjectContactsData ?? [])
          .filter(Boolean)
          .sort(({ contactId: a }, { contactId: b }) =>
            sortContactsHelper(getContactById(a), getContactById(b))
          ),
        'externalProjectContactId'
      );
    const contacts: Contact[] = [
      ...internalProjectContacts,
      ...externalProjectContacts,
    ]
      .map((contact) => (contact ? getContactById(contact.contactId) : null))
      .filter(Boolean)
      .sort(sortContactsHelper);
    const contactIds: ContactId[] = contacts.map(({ contactId }) => contactId);
    const internalProjectContactContactIds: ContactId[] =
      internalProjectContacts.map(({ contactId }) => contactId);
    const externalProjectContactContactIds: ContactId[] =
      externalProjectContacts.map(({ contactId }) => contactId);
    const internalProjectContactIds: InternalProjectContactId[] =
      internalProjectContacts.map(
        ({ internalProjectContactId }) => internalProjectContactId
      );
    const externalProjectContactIds: ExternalProjectContactId[] =
      externalProjectContacts.map(
        ({ externalProjectContactId }) => externalProjectContactId
      );
    const projectContacts: ProjectContact[] = [
      ...internalProjectContacts.map((internalProjectContact) => ({
        projectContactId: internalProjectContact.internalProjectContactId,
        contact: getContactById(internalProjectContact.contactId),
      })),
      ...externalProjectContacts.map((externalProjectContact) => ({
        projectContactId: externalProjectContact.externalProjectContactId,
        contact: getContactById(externalProjectContact.contactId),
      })),
    ].sort((a, b) => sortContactsHelper(a.contact, b.contact));
    const internalProjectContactContacts: Contact[] =
      internalProjectContacts.map(({ contactId }) => getContactById(contactId));
    const externalProjectContactContacts: Contact[] =
      externalProjectContacts.map(({ contactId }) => getContactById(contactId));
    return {
      projectContacts,
      internalProjectContacts,
      externalProjectContacts,
      contacts,
      contactIds,
      internalProjectContactContactIds,
      externalProjectContactContactIds,
      internalProjectContactIds,
      externalProjectContactIds,
      internalProjectContactContacts,
      externalProjectContactContacts,
    };
  }, [
    internalProjectsContactData,
    externalProjectContactsData,
    getContactById,
  ]);
};
